/* eslint-disable */

import axios from 'axios';
import router from '../Routes';
import _ability from '../config/ability';
import _ from 'lodash';
import LogRocket from 'logrocket';
import { isInResponsibleList } from '../utils/permissions';
import microsoftAuth from '../services/microsoftAuth';
import { createLoginLogger } from '../utils/loginLogger';
import { syncSiteMask } from '../utils/phiMaskState';
import { getIdToken as getFirebaseIdToken } from '../pages/MyAccount/components/firebaseIdToken';
import { SITES_CLIENT_SELECT } from '../sql/sitesColumns';
import { safeSnapshot } from './firestoreListen';
import { DEMO_SESSION_HINT_KEY, DEMO_ENDED_KEY, isDemoClaims, logoutPath } from '../utils/demo/demoSession';
import { demoLogRocketPlan } from '../utils/demo/demoLogRocket';

//axios.defaults.baseURL = 'https://y0gky23tog.execute-api.us-east-1.amazonaws.com/dev';
axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL

// Module-scoped in-flight cache for dedup'ing concurrent identical fetches.
// Background: a single Vuex flag flip (e.g. `patientNeedsUpdating`) used to
// trigger N component watchers in parallel, each dispatching its own
// /runquery call (PROD 2026-04-27 saw 30+ identical patient-list calls in
// under 1.5 s). Keying by payload string lets concurrent dispatches with
// the same args share one promise; different payloads run independently.
const inFlightFetches = Object.create(null);

// Demo site (Task 6.6): LogRocket starts once per page; "View as" re-runs handleSelectSite and only re-identifies.
let demoLogRocketStarted = false;

function dedupInFlight(name, key, fetchFn) {
    const bucket = inFlightFetches[name] || (inFlightFetches[name] = new Map());
    if (bucket.has(key)) return bucket.get(key);
    const p = (async () => {
        try { return await fetchFn(); }
        finally { bucket.delete(key); }
    })();
    bucket.set(key, p);
    return p;
}

/**
 * SQL for the global patients list (Header search, follow-up patient pickers,
 * etc). Single source of truth — used by BOTH the bootstrap fetch on login
 * (getSitesAndEmployeesAndStatusesAndListsAndSettingsAndPatientsAndProviders)
 * AND the standalone refresh action (getPatients).
 *
 * Filter shape (intentional, mirrors the original bootstrap query):
 *   - Patient (PatStatus=0) or Prospective (PatStatus=6). OD Prospective is a
 *     pre-first-visit record some offices enter every new patient as; excluding it
 *     hid new patients from search exactly when staff need to send intake forms
 *     (Harrisburg Smiles, 2026-08-28). Non-Patient/Inactive/Archived/Deleted/
 *     Deceased (1-5) stay excluded.
 *   - Has a non-empty first + last name
 *   - AND one of:
 *       * has no completed/scheduled appointments at all (A is null), OR
 *       * last completed visit was within the last 5 years, OR
 *       * has an upcoming scheduled appointment (AptStatus=1)
 *
 * Why both queries diverged historically: the standalone getPatients was
 * a simpler PatStatus=0 filter that returned far more rows than login's
 * filtered set. Triggered a confusing UX where the search box showed
 * "List is empty" right after login (login's filter returned 0) but
 * clicking the refresh icon next to it would suddenly show 3000+ rows
 * (standalone query). Now both return the same set, so refresh is a true
 * "re-run the same query".
 */
function buildPatientsListQuery(siteID) {
    const db = `teamio_${siteID}`;
    return `set @sqlDateFormatValue :=
        (select
        case
            when DateFormat = 'DD/MM/YYYY' then '%d-%m-%Y'
            else '%m-%d-%Y'
        end as DateFormat
        from teamio.sites where SiteID='${siteID}'); SELECT distinct p.PatNum, LName, FName, A.Lastvisit, CONCAT(LName, ', ', FName) as Patient, WirelessPhone, Birthdate, TxtMsgOk,
        CONCAT(LName, ', ', FName, ' (', DATE_FORMAT(Birthdate,@sqlDateFormatValue), ')') as PatientWithDOB
        FROM \`${db}\`.patient p
        LEFT JOIN (
            select PatNum, MAX(apt.AptDateTime) as LastVisit, apt.AptStatus from \`${db}\`.appointment apt
            where apt.AptStatus=2 or apt.AptStatus = 1
            group by apt.PatNum
        ) A on A.PatNum = p.PatNum
        where LName != '' and FName != '' and PatStatus IN (0, 6)  and (A.AptStatus is null  or (year(A.Lastvisit) >= YEAR(DATE_SUB(curdate(), INTERVAL 5 YEAR)) or A.AptStatus=1)  )
        order by LName;`;
}

export default {

    namespaced: true,
    state: {
        loading: true,
        customerID: localStorage.getItem('teamioCustomerID') || '',
        userID: localStorage.getItem('teamioUserID') || '',
        siteID: localStorage.getItem('teamioSiteID') || '',
        user: JSON.parse(localStorage.getItem('user')) || null,
        sites: JSON.parse(localStorage.getItem('sites')) || null,
        employees: JSON.parse(localStorage.getItem('employees')) || null,
        lists: JSON.parse(localStorage.getItem('lists')) || null,
        brokenApptAdjustments: localStorage.getItem('brokenApptAdjustments') || '',
        brokenApptCodes: localStorage.getItem('brokenApptCodes') || '',
        // [] not null: the phiMaskState reconcile (and the quota fallback below)
        // legitimately REMOVE this cache, and a null roster crashes VueMultiselect
        // internals (Header search) and FormsHome::patientOptions (2026-08-31).
        patients: JSON.parse(localStorage.getItem('patients')) || [],
        privacyOn: JSON.parse(localStorage.getItem('privacyOn')) || false, 
        patientNeedsUpdating: false,
        employeeNeedsUpdating: false,
        settings: JSON.parse(localStorage.getItem('settings')) || null,
        statuses: null,
        rules: JSON.parse(localStorage.getItem('rules')) || [],
        assets: [],
        status: '',
        providers: JSON.parse(localStorage.getItem('providers')) || null, 
        procCodes: JSON.parse(localStorage.getItem('procCodes')) || null,
        firestoreListeners: [],
        // Clinics feature - HQ siteID and flag for multi-clinic customers
        hqSiteID: localStorage.getItem('hqSiteID') || null,
        usingClinics: parseInt(localStorage.getItem('usingClinics')) || 0,
        // Demo site (Try Teamio). The hint only covers the first paint; firebase.config.js re-derives the flag from
        // the ID token's claims on every boot. UI only — the server decides what a demo session may do.
        isDemo: localStorage.getItem(DEMO_SESSION_HINT_KEY) === 'true',
        // The demo token's prospectID claim, set at the link sign-in and on "View as" (LogRocket keys on it, Task 6.6).
        demoProspectID: null,
    },

    mutations: {

        setAssets(state, payload) {
            state.assets = payload;
        },

        setEmployees(state, payload) {
            state.employees = payload;
        },

        setSites(state, payload) {
            state.sites = payload;
        },

        // sites are hydrated from localStorage on every reload (line ~40), so
        // a stale PaymentProvider survives a hard refresh. Update both layers.
        setSitePaymentProvider(state, { siteID, provider }) {
            if (!Array.isArray(state.sites)) return;
            const site = state.sites.find(s => s.SiteID === siteID);
            if (!site) return;
            site.PaymentProvider = provider;
            try {
                localStorage.setItem('sites', JSON.stringify(state.sites));
            } catch (err) {
                console.warn('setSitePaymentProvider: localStorage update failed', err);
            }
        },

        setloading(state, payload) {
            state.loading = payload;
        },

        setDemoProspectID(state, payload) {
            state.demoProspectID = typeof payload === 'string' ? payload : null;
        },

        setIsDemo(state, payload) {
            const wasDemo = state.isDemo === true;
            state.isDemo = payload === true;
            try {
                if (state.isDemo) localStorage.setItem(DEMO_SESSION_HINT_KEY, 'true');
                else localStorage.removeItem(DEMO_SESSION_HINT_KEY);
                // /try reads this once and says "Your demo has ended" (Task 6.3).
                if (wasDemo && !state.isDemo) sessionStorage.setItem(DEMO_ENDED_KEY, '1');
            } catch (e) { /* storage blocked: the flag still lives in state */ }
        },

        setSettings(state, payload) {
            state.settings = payload;
            // Mirror the site-wide demo flag into the PHI-mask state so the axios
            // interceptor (which runs outside any component) can read it. Every
            // path that loads settings funnels through this one mutation.
            syncSiteMask(payload);
        },

        setStatuses(state, payload) {
            state.statuses = payload;
        },

        setCustomerID(state, payload) {
            state.customerID = payload;
        },

        setUserID(state, payload) {
            state.userID = payload;
        },

        setUser(state, payload) {
            state.user = payload;
        },

        setSiteID(state, payload) {
            state.siteID = payload;
        },

        setLists(state, payload) {
            state.lists = payload;
        },


        setRules(state, payload) {
            state.rules = payload;
            try { localStorage.setItem('rules', JSON.stringify(payload)); } catch(e) {}
        },

        setPatients(state, payload) {
            state.patients = Array.isArray(payload) ? payload : []
        },

        setProviders(state, payload) {
            state.providers = payload;
        },

        setPatientNeedsUpdating(state, payload) {
            state.patientNeedsUpdating = payload;
        },

        setEmployeeNeedsUpdating(state, payload) {
            state.employeeNeedsUpdating = payload;
        },

        setPrivacyOn(state, payload) {            
            state.privacyOn = payload;
            localStorage.setItem('privacyOn', JSON.stringify(payload));
        },

        setProcCodes(state, payload) {
            state.procCodes = payload;
            localStorage.setItem('procCodes', JSON.stringify(payload));
        },

        // Clinics feature mutations
        setHqSiteID(state, payload) {
            state.hqSiteID = payload;
            localStorage.setItem('hqSiteID', payload);
        },

        setUsingClinics(state, payload) {
            state.usingClinics = payload;
            localStorage.setItem('usingClinics', payload);
        },

        addFirestoreListener(state, payload) {
            state.firestoreListeners.push(payload);
        },

        removeFirestoreListener(state, payload) {
            state.firestoreListeners = state.firestoreListeners.filter(l => l !== payload);
        },

        clearFirestoreListeners(state) {
            state.firestoreListeners.forEach(function(l) {
                if(typeof l === 'function')
                    l();
            });
            state.firestoreListeners = [];
        },
    },

    getters : {
        isLoggedIn: state => state.user !== null && state.user !== undefined,
        isDemo: state => state.isDemo === true,
        user: state => state.user,
        userID: state => state.userID,
        customerID: state => state.customerID,
        siteID: state => state.siteID,
        sites: state => state.sites,
        employeeName: (state) => (id) => {
            let temp = state.employees.find(e => e.EmployeeID === id);
            if(temp)
                return temp.Name;
            else
                return '';
        },        
        
        getEmployee: state => id => state.employees.find(e => e.EmployeeID === id),
        list: state => id => state.lists.find(e => e.ListID === id),
        activeEmployees: state => state.employees.filter(e => e.Active === 1),        
        getSite: state => siteID => {
            let site = state.sites.find(s => s.SiteID === siteID);
            if(site)
                return site;
        },

        getPatient: state => patNum => {
            let patient = (state.patients || []).find(s => s.PatNum === patNum);
            if(patient)
                return patient;
        },

        getProvNum: state => name => {
            let temp = state.providers.find(p => p.LName + ", " + p.FName === name);
            if(temp)
                return temp.ProvNum;
            else
                return 0;
        },

        getProvColor: state => provNum => {
            let temp = state.providers.find(p => p.ProvNum === provNum);
            if(temp)
                return temp.ProvColor;
            else
                return 0;
        },

        getProvNumFromEmployeeID: state => employeeID => {
            let temp = state.providers.find(p => p.EmployeeID && p.EmployeeID === employeeID);
            if(temp)
                return temp.ProvNum;
        },

        getProviderFromEmployeeID: state => employeeID => {
            let temp = state.providers.find(p => p.EmployeeID && p.EmployeeID === employeeID);
            if(temp)
                return temp;
        },

        getProviderFromProvNum: state => provNum => {            
            let temp = state.providers.find(p => p.ProvNum === provNum);
            if(temp)
                return temp;
        }
    },

    actions: {

        async loadRules({commit, state}, payload) {
            //console.log(`in loadRules now with payload: ${JSON.stringify(payload)}`);
            let _settings = _.keyBy(payload.settings, 'Property');
            // `sites` feeds Schedule - Chart Audit's owner-exe-version rule (config/ability.js).
            let temp = _ability(payload.user, _settings, { sites: payload.sites || state.sites });
            commit('setRules', temp.rules);            
        },

        async getSettings({commit, dispatch}, payload) {
            try {
                //dispatch('alert/setLoading', true, {root: true});
                let sqlQuery = `select Property, PropertyValue from teamio.settings where SiteID='${payload.siteID}' order by Property;`;
                let sql = {
                    sqlQuery: sqlQuery
                };
                //console.log('Query:' + sqlQuery);
                let response = await axios.post(`/runquery`, sql, { crossdomain: true });
                commit('setSettings', response.data);
                localStorage.setItem('settings', JSON.stringify(response.data));
                //dispatch('alert/setLoading', false, {root: true});
                //dispatch('alert/setLoaded', true, {root: true});
            }
            catch(err) {
                console.log('Error getting Settings:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },


        async getPatients({commit, dispatch}, payload) {
            return dedupInFlight('getPatients', String(payload && payload.siteID || ''), async () => {
                try {
                    // Same query the bootstrap fetch uses on login. See
                    // buildPatientsListQuery for the filter rationale.
                    let sql = {
                        sqlQuery: buildPatientsListQuery(payload.siteID)
                    };
                    let response = await axios.post(`/runquery`, sql, { crossdomain: true });
                    //console.log("response**************:",response);
                    if(response.data && response.data.length > 0) {
                        commit('setPatients', response.data[1]);
                        try {
                            localStorage.setItem('patients', JSON.stringify(response.data[1]));
                        } catch (error) {
                            console.log(error);
                        }
                        return response.data[1]; // Return the data for Promise.all
                    }
                    return []; // Return empty array if no data
                } catch(err) {
                    console.error('Error getting patients:', err);
                    return []; // Return empty array on error
                }
            });
        },

        async getProcedureTypes({state}, payload) {
            try {
                let hqSiteID = state.hqSiteID || payload.siteID;
                let hqDb = `teamio_${hqSiteID}`;
                let sql = {
                    sqlQuery: `SELECT DefNum, ItemName FROM \`${hqDb}\`.definition WHERE Category = 11 AND IsHidden = 0;`
                };
                let response = await axios.post('/runquery', sql, { crossdomain: true });
                return response.data;
            } catch (err) {
                console.error('Error getting procedure types:', err);
                return [];
            }
        },

        async getProcCodes({commit, dispatch, state}, payload) {
            try {
                //dispatch('alert/setLoading', true, {root: true});
                // Clinics feature: procedurecode is HQ-only table, use hqSiteID from state with fallback
                let hqSiteID = state.hqSiteID || payload.siteID;
                let hqDb = `teamio_${hqSiteID}`;
                // Include CodeNum for workflow matching - proc codes are stored by CodeNum in workflow data
                let procCodesQuery  = `select pc.CodeNum, pc.ProcCode, pc.Descript from \`${hqDb}\`.procedurecode pc;`;
                let sql = {
                    sqlQuery: procCodesQuery
                };
                //console.log('Query:' + sqlQuery);
                let response = await axios.post(`/runquery`, sql, { crossdomain: true });
                commit('setProcCodes', response.data);
                //dispatch('alert/setLoading', false, {root: true});
                //dispatch('alert/setLoaded', true, {root: true});
                return response.data; // Return the data for Promise.all
            }
            catch(err) {
                console.log('Error getting Proc Codes for a site:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                return []; // Return empty array on error
            }
        },

        async checkifPatientOrEmployeeNeedsUpdating({ commit, rootState, dispatch }, payload) {
            const miscCollectionRef = rootState.collection(rootState.db, 'misc');
            const q = rootState.query(miscCollectionRef, rootState.where('siteID', '==', payload.siteID), rootState.limit(5));
        
            const unsubscribe = safeSnapshot(q, (messagesSnapshot) => {
                if (!messagesSnapshot.empty) {
                    const updates = { patientNeedsUpdating: false, employeeNeedsUpdating: false };
                    
                    messagesSnapshot.forEach((message) => {
                        const msg = message.data();
                        console.log('patient or employee needs updating', msg);
                        if (msg.patientUpdated) {
                            updates.patientNeedsUpdating = true;
                        }
                        if (msg.employeeUpdated) {
                            updates.employeeNeedsUpdating = true;
                        }
                    });
                    
                    // Use debounced update to prevent rapid state changes
                    dispatch('debouncedUpdatePatientEmployeeStatus', updates);
                } else {
                    // Use debounced update for consistency
                    dispatch('debouncedUpdatePatientEmployeeStatus', { 
                        patientNeedsUpdating: false, 
                        employeeNeedsUpdating: false
                    });
                }
            }, { source: 'customer/needsUpdating' });

            commit('addFirestoreListener', unsubscribe);
            return unsubscribe;
        },
        
        // Debounced update function to prevent rapid state changes
        debouncedUpdatePatientEmployeeStatus: _.debounce(function({ commit }, updates) {
            commit('setPatientNeedsUpdating', updates.patientNeedsUpdating);
            commit('setEmployeeNeedsUpdating', updates.employeeNeedsUpdating);
        }, 250),

        async removePatientNeedsUpdating({ commit, rootState, dispatch }, payload) {
            const miscCollectionRef = rootState.collection(rootState.db, 'misc');
            const q = rootState.query(miscCollectionRef, rootState.where('siteID', '==', payload.siteID));
        
            const querySnapshot = await rootState.getDocs(q);
            querySnapshot.forEach(async (doc) => {
                const msg = doc.data();
                if (msg.patientUpdated) {
                    await rootState.deleteDoc(doc.ref);
                    console.log(`patient needs updating doc removed`);
                }
            });
        },

        async removeEmployeeNeedsUpdating({ commit, rootState, dispatch }, payload) {
            const miscCollectionRef = rootState.collection(rootState.db, 'misc');
            const q = rootState.query(miscCollectionRef, rootState.where('siteID', '==', payload.siteID));
        
            const querySnapshot = await rootState.getDocs(q);
            querySnapshot.forEach(async (doc) => {
                const msg = doc.data();
                if (msg.employeeUpdated) {
                    await rootState.deleteDoc(doc.ref);
                    console.log(`employee needs updating doc removed`);
                }
            });
        },

        async getSitesAndEmployeesAndStatusesAndListsAndSettingsAndPatientsAndProviders ({commit, dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                let db = `teamio_${payload.siteID}`;

                // Lookup HQ siteID for Clinics feature - HQ-only tables (provider, userod) must use HQ database
                // UsingClinics is on teamio.customers table, HQ SiteID is on teamio.sites where ClinicNum = 0
                let hqLookupQuery = {
                    sqlQuery: `SELECT COALESCE(c.UsingClinics, 0) as UsingClinics, s.SiteID as HqSiteID
                               FROM teamio.customers c
                               LEFT JOIN teamio.sites s ON s.CustomerID = c.CustomerID AND s.ClinicNum = 0
                               WHERE c.CustomerID = '${payload.customerID}'
                               LIMIT 1`
                };
                let hqLookupResult = await axios.post('/runquery', hqLookupQuery, { crossdomain: true });
                let hqRow = hqLookupResult.data && hqLookupResult.data[0] ? hqLookupResult.data[0] : null;
                let usingClinics = (hqRow && hqRow.UsingClinics !== undefined && hqRow.UsingClinics !== null) ? hqRow.UsingClinics : 0;
                let hqSiteID = (usingClinics === 1 && hqRow && hqRow.HqSiteID) ? hqRow.HqSiteID : payload.siteID;
                let hqDb = `teamio_${hqSiteID}`;

                // Store HQ siteID and usingClinics in Vuex state for other functions to use
                commit('setHqSiteID', hqSiteID);
                commit('setUsingClinics', usingClinics);

                /*
                    Important note about employees - I think the reason I'm pulling ALL employees here and not just where Active = 1 is because
                    if a task was assigned to an employee and he/she's been deleted then it might have broken something.
                    I believe when showing employee dropdown I'm already ONLY showing those that are active anyways so I think we're ok here
                */
                // Named columns, never `select *`: this row is cached in localStorage and the
                // sites table carries secrets (see src/sql/sitesColumns.js).
                let sqlQuery = `select ${SITES_CLIENT_SELECT} from teamio.sites where CustomerID='${payload.customerID}';
                                select e.*, r.Role from teamio.employees e, teamio.roles r where e.SiteID='${payload.siteID}' and e.RoleID = r.RoleID;
                                select TaskTypeID, ClientSystemFollowupStatus, TeamioFollowupStatusID, ODDefNum from teamio.\`client-followupstatuses-mapping\` where SiteID='${payload.siteID}' ORDER BY TaskTypeID;
                                select * from teamio.lists where SiteID='${payload.siteID}';
                                select Property, PropertyValue from teamio.settings where SiteID='${payload.siteID}' order by Property;
                                -- select pr.ProvNum, pr.FName, pr.MI, pr.LName, pr.Abbr, pr.Suffix, pr.IsSecondary, pr.ProvColor from \`${db}\`.provider pr where pr.IsHidden=0 AND pr.IsNotPerson=0;

                                select pr.ProvNum, pr.FName, pr.MI, pr.LName, pr.Abbr, pr.Suffix, pr.IsSecondary, pr.ProvColor, temp.EmployeeID, temp.UserNum as UserNum
                                from \`${hqDb}\`.provider pr LEFT JOIN (
                                    select uo.ProvNum, uo.UserNum, tem.SiteID, tem.EmployeeID from \`${hqDb}\`.userod uo
                                    left join teamio.employees tem on tem.ODUserNum = uo.UserNum and tem.SiteID='${payload.siteID}' and tem.Active = 1 
                                ) temp
                                on pr.ProvNum = temp.ProvNum and temp.SiteID='${payload.siteID}'
                                where pr.IsHidden=0 AND pr.IsNotPerson=0;
                `;

                let sql = {
                    sqlQuery: sqlQuery
                };
                //console.log('Query:' + sqlQuery);

                // Patients list — same shared SQL the standalone getPatients
                // action runs. See buildPatientsListQuery for the filter
                // rationale.
                const sql2 = {
                    sqlQuery: buildPatientsListQuery(payload.siteID)
                };
                    
                //console.log('Query2:' + sql2);
                
                let promise1 = axios.post(`/runquery`, sql, { crossdomain: true });
                let promise2 = axios.post(`/runquery`, sql2, { crossdomain: true });
                const promises = [promise1, promise2];
                let response;

                // Large /runquery responses come back gzip-compressed via API Gateway
                // (Content-Encoding: gzip on the Lambda side); the browser decompresses
                // transparently, so axios always sees plain JSON.
                response = await Promise.all(promises);

                if(response.length > 0) {
                    commit('setSites', response[0].data[0]);
                    commit('setEmployees', response[0].data[1]);
                    commit('setStatuses', response[0].data[2]);
                    commit('setLists', response[0].data[3]);
                    commit('setSettings', response[0].data[4]);                
                    commit('setProviders', response[0].data[5]);
                    commit('setPatients', response[1].data[1]);

                    //console.log('providers', response[0].data[5]);
                    
                    try {
                        localStorage.setItem('sites', JSON.stringify(response[0].data[0]));
                        localStorage.setItem('employees', JSON.stringify(response[0].data[1]));
                        //localStorage.setItem('statuses', JSON.stringify(response.data[2]));
                        localStorage.setItem('lists', JSON.stringify(response[0].data[3]));
                        localStorage.setItem('settings', JSON.stringify(response[0].data[4]));                
                        localStorage.setItem('providers', JSON.stringify(response[0].data[5]));                    
                        let _settings = _.keyBy(response[0].data[4], 'Property');
                        localStorage.setItem('brokenApptAdjustments', _settings.BROKEN_APPOINTMENTS_SELECTED_ADJUSTMENTS.PropertyValue);
                        localStorage.setItem('brokenApptCodes', _settings.BROKEN_APPOINTMENTS_CODES.PropertyValue);
                        localStorage.setItem('country', _settings.GENERAL_COUNTRY ? _settings.GENERAL_COUNTRY.PropertyValue : 'USA');
                        // Patient blob can exceed browser quota (~5-10MB) for large/long-tenured
                        // practices. Vuex state is already populated above via commit('setPatients');
                        // this cache is best-effort, so don't let a quota failure break login.
                        try {
                            localStorage.setItem('patients', JSON.stringify(response[1].data[1]));
                        } catch (patientsCacheErr) {
                            console.warn('Skipping patients localStorage cache (quota exceeded or unavailable):', patientsCacheErr && patientsCacheErr.message);
                            try { localStorage.removeItem('patients'); } catch (_) { /* ignore */ }
                        }
                    } catch (error) {
                        console.log(error);
                        throw error;
                    } finally {
                        dispatch('alert/setLoading', false, {root: true});
                        dispatch('alert/setLoaded', true, {root: true});
                    }                    
                } else {
                    dispatch('alert/setErrored', true, {root: true});
                    dispatch('alert/setMessage', `Something seems wrong. Please contact Teamio Support: nilay@teamio.app`, {root: true});
                }                
                
                return response;
            }
            catch(err) {
                console.error('Error getting sites, employees, statuses, lists, settings, patients, providers:', err.message);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw err;
            }
        },

        async getCustomers( {commit, dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                // `{}` not nothing: an empty-body POST goes out without the login token (authHeaderPolicy), and /getCustomers is Super Admin only.
                let response = await axios.post(`/getCustomers`, payload || {}, { crossdomain: true });
                dispatch('alert/setLoading', false, {root: true});
                dispatch('alert/setLoaded', true, {root: true});
                return response;
            }
            catch(err) {
                console.log('Error getting customers:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        async getSites( {commit, dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                let sqlQuery = `select ${SITES_CLIENT_SELECT} from teamio.sites where CustomerID='${payload.customerID}';`;
                let sql = {
                    sqlQuery: sqlQuery
                };
                let response = await axios.post(`/runquery`, sql, { crossdomain: true });
                commit('setSites', response.data);
                localStorage.setItem('sites', JSON.stringify(response.data));
                dispatch('alert/setLoading', false, {root: true});
                dispatch('alert/setLoaded', true, {root: true});
            }
            catch(err) {
                console.log('Error getting sites:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        async getAllActiveSites({commit, dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                let sqlQuery = `SELECT SiteID, Name, CustomerID, Active FROM teamio.sites WHERE Active = 1 ORDER BY Name;`;
                let sql = {
                    sqlQuery: sqlQuery
                };
                let response = await axios.post(`/runquery`, sql, { crossdomain: true });
                commit('setSites', response.data);
                localStorage.setItem('allActiveSites', JSON.stringify(response.data));
                dispatch('alert/setLoading', false, {root: true});
                dispatch('alert/setLoaded', true, {root: true});
                return response.data;
            }
            catch(err) {
                console.log('Error getting all active sites:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw err;
            }
        },

        async getStatuses( {commit, dispatch}, payload) {
            try {
                //dispatch('alert/setLoading', true, {root: true});
                let sqlQuery = `select FollowupStatusID, TaskTypeID, ClientSystemFollowupStatus, TeamioFollowupStatusID, ODDefNum from teamio.\`client-followupstatuses-mapping\` where SiteID='${payload}' ORDER BY TaskTypeID;`;
                let sql = {
                    sqlQuery: sqlQuery
                };
                let response = await axios.post(`/runquery`, sql, { crossdomain: true });
                commit('setStatuses', response.data);
                //dispatch('alert/setLoading', false, {root: true});
                //dispatch('alert/setLoaded', true, {root: true});
            }
            catch(err) {
                console.log('Error getting client statuses:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        async saveSettings( {commit, dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post(`/save-settings`, payload, { crossdomain: true });
                dispatch('alert/setLoading', false, {root: true});
                dispatch('alert/setLoaded', true, {root: true});
                /*
                dispatch('alert/setMessage', 'Settings saved successfully!', {root: true});
                dispatch('alert/setDismissSuccessCountDown', 3, {root: true});
                */
            }
            catch(err) {
                console.log('Error saving settings:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        // Upsert individual settings - safe for GENERAL settings as it doesn't bulk delete
        async upsertSettings( {commit, dispatch}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                let response = await axios.post(`/upsert-settings`, payload, { crossdomain: true });
                dispatch('alert/setLoading', false, {root: true});
                dispatch('alert/setLoaded', true, {root: true});
            }
            catch(err) {
                console.log('Error upserting settings:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
                throw err; // Re-throw so the caller can handle it
            }
        },

        async getAssets( {commit, dispatch, rootState}, payload) {
            try {
                
                commit('setAssets', tempAssets);
            }
            catch(err) {
                console.log('Error getting Assets:', err);
                dispatch('alert/setErrored', true, {root: true});
                dispatch('alert/setMessage', err, {root: true});
            }
        },

        async getEmployees( {commit, dispatch, rootState}, payload) {
            return dedupInFlight('getEmployees', String(payload && payload.sqlQuery || ''), async () => {
                try {
                    dispatch('alert/setLoading', true, {root: true});
                    let sql = {
                        sqlQuery: payload.sqlQuery
                    };
                    let response = await axios.post(`/runquery`, sql, { crossdomain: true });
                    commit('setEmployees', response.data);
                    localStorage.setItem('employees', JSON.stringify(response.data));
                    dispatch('alert/setLoading', false, {root: true});
                    dispatch('alert/setLoaded', true, {root: true});
                    return response.data; // Return the data for Promise.all
                }
                catch(err) {
                    console.log('Error getting employees:', err);
                    dispatch('alert/setErrored', true, {root: true});
                    dispatch('alert/setMessage', err, {root: true});
                    return []; // Return empty array on error
                }
            });
        },

        async fbGoogleLogin({commit, dispatch, rootState}, attemptId) {
            const logger = createLoginLogger('google', attemptId);
            try {
                let provider = new rootState.GoogleAuthProvider();
                //console.log('provider', provider);
                // For other scopes - https://developers.google.com/identity/protocols/googlescopes
                provider.addScope('https://www.googleapis.com/auth/userinfo.profile');

                // Force account selection popup every time - fixes shared computer issue
                // where Google auto-selects the last logged-in account
                provider.setCustomParameters({
                    prompt: 'select_account'
                });

                const auth = await rootState.getAuth();
                logger.step('fb:signInWithPopup-begin');
                let result = await rootState.signInWithPopup(auth, provider);
                //console.log('result', result);

                const user = result.user;
                logger.step('fb:signInWithPopup-success', { email: user && user.email });
                let token = user.getIdToken();
                logger.step('fb:getIdToken');

                /*
                const credential = rootState.GoogleAuthProvider.credentialFromResult(result);
                console.log('credential', credential);                
                const token = credential.idToken;                
                const user = result.user;
                */
                //console.log(`user: ${JSON.stringify(user)}`);
                //let idToken = await rootState.firebase.auth().currentUser.getIdToken();
                return token;
            } catch (err) {
                logger.error('fb:google-popup', err);
                throw err;
            }
        },

        async watchMetaData({ commit, dispatch, rootState, state }, uid) {
            // Prevent duplicate listeners - check if we already have a listener for this UID
            const existingListenerIndex = state.firestoreListeners.findIndex(
                l => l && l._metadataUid === uid
            );

            if (existingListenerIndex !== -1) {
                console.log(`[watchMetaData] Listener already exists for uid: ${uid}, skipping duplicate`);
                return state.firestoreListeners[existingListenerIndex];
            }

            const metadataDocRef = rootState.doc(rootState.db, 'metadata', uid);

            const unsubscribe = safeSnapshot(metadataDocRef, async (metadataSnapshot) => {
                if (metadataSnapshot.exists) {
                    const metadataTemp = metadataSnapshot.data();
                    if(metadataTemp) {
                        console.log('[watchMetaData] metadata changed:', metadataTemp);

                        if (metadataTemp.revokeTime) {
                            console.log(`[watchMetaData] revoke signal received. now logging out`);
                            try {
                                await dispatch('clearMetaData', uid);
                                await dispatch('logout', { reload: true, msg: 'Your session has been revoked. Please login again' });
                            } catch (error) {
                                console.log('[watchMetaData] Error during logout:', error);
                            }
                        }
                    }
                }
            }, { source: 'customer/metadata' });

            // Tag the unsubscribe function with the UID so we can identify it later
            unsubscribe._metadataUid = uid;

            // Store the listener so it can be cleaned up on logout
            commit('addFirestoreListener', unsubscribe);

            return unsubscribe;
        },

        async clearMetaData({ commit, dispatch, rootState }, uid) {
            const metadataDocRef = rootState.doc(rootState.db, 'metadata', uid);
            await rootState.deleteDoc(metadataDocRef);
            //console.log(`uid: ${uid} removed from fb`);
        },

        async googleLogin({commit, dispatch, rootState}, payload) {
            // Back-compat: payload may be a raw idToken string (legacy) or
            // { idToken, loginAttemptId }. The body sent to the backend stays
            // the raw idToken; the attempt id rides as a query param so it lines
            // up with the backend CloudWatch logs.
            const idToken = (payload && typeof payload === 'object') ? payload.idToken : payload;
            const attemptId = (payload && typeof payload === 'object') ? payload.loginAttemptId : undefined;
            const logger = createLoginLogger('google', attemptId);
            let res;
            try {
                dispatch('alert/setLoading', true, {root: true});
                const url = `/google-login${attemptId ? `?loginAttemptId=${encodeURIComponent(attemptId)}` : ''}`;
                logger.step('store:post-google-login');
                res = await axios.post(url, idToken, { crossdomain: true });
                logger.step('store:google-login-response', { status: res && res.status });
                dispatch('alert/setLoading', false, {root: true});
                dispatch('alert/setLoaded', true, {root: true});
                return res;
            }
            catch(err) {
                logger.error('store:google-login', err);
                dispatch('alert/setErrored', true, {root: true});

                const originalResponseData = err.response && err.response.data;
                let messageForAlertModule;

                if(originalResponseData) {
                    messageForAlertModule = originalResponseData; // Default to original
                    if (originalResponseData === 'User not found in the system. Please try again or contact us at nilay@teamio.app') {
                        messageForAlertModule = `
                        <h6>There\'s an issue logging in, please check if any one or more of the following conditions apply:</h6>
                        <b-card class="border-0 mb-xlg background-cover">
                                <div class="d-flex">
                                <i class="glyphicon glyphicon-chevron-right mr-3"></i>
                                    <p class="fs-13 fw-semi-bold">You are logged into another gmail account on this brower, in this case, go to gmail.com and log out! If still having trouble, view this <a href="https://teamio.app/help/logging-in/" target="_blank">helpful video</a> to help you log in.</p>
                                </div>
                                <div class="d-flex">
                                    <i class="glyphicon glyphicon-chevron-right mr-3"></i>
                                    <p class="fs-13 fw-semi-bold">If you\'re a team member, make sure your Administrator / Office Manager has informed us to add you onto Teamio Account.</p>
                                </div>
                                <div class="d-flex">
                                    <i class="glyphicon glyphicon-chevron-right mr-3"></i>
                                    <p class="fs-13 fw-semi-bold">Your account may be deactivated because you may have passed the Trial Period. If this is NOT the case or you\'d like to renew, please contact us right away via email at nilay@teamio.app</p>
                                </div>
                                </b-card>
                        `;
                    }
                    dispatch('alert/setMessage', messageForAlertModule, {root: true});
                } else { // Handles cases where err.response or err.response.data is null/undefined
                    dispatch('alert/setMessage', err.message || 'An unknown error occurred', {root: true});
                }
                throw err; // Throw the original err object; its err.response.data is NOT modified by this block for Login.vue's use.
            } finally {
                // return res; // <--- REMOVED THIS LINE
            }
        },

        async microsoftLogin({commit, dispatch, rootState}, microsoftToken) {
            const attemptId = microsoftToken && microsoftToken.loginAttemptId;
            const logger = createLoginLogger('microsoft', attemptId);
            // `quiet`: the session-health guard's background re-auth (src/plugins/sessionHealthGuard.js).
            // It must not flash the loading overlay or paint a login error on the page the user is
            // working on — a failure there just falls back to the "sign in again" prompt. The flag is
            // stripped from the body, so the request is unchanged.
            const quiet = !!(microsoftToken && microsoftToken.quiet);
            let body = microsoftToken;
            if (quiet) {
                body = Object.assign({}, microsoftToken);
                delete body.quiet;
            }
            let res;
            try {
                if (!quiet) dispatch('alert/setLoading', true, {root: true});
                const url = `/microsoft-login${attemptId ? `?loginAttemptId=${encodeURIComponent(attemptId)}` : ''}`;
                logger.step('store:post-microsoft-login');
                res = await axios.post(url, body, { crossdomain: true });
                logger.step('store:microsoft-login-response', {
                    status: res && res.status,
                    hasFirebaseToken: !!(res && res.data && res.data.firebaseToken),
                });

                // If backend returns a Firebase custom token, sign in with it
                if (res.data && res.data.firebaseToken) {
                    const firebaseToken = res.data.firebaseToken;
                    const { getAuth, signInWithCustomToken } = await import('firebase/auth');

                    const auth = getAuth();
                    logger.step('store:firebase-signin-begin');
                    await signInWithCustomToken(auth, firebaseToken);
                    logger.step('store:firebase-signin-success');

                    // Return the user data for site selection
                    res.data = res.data.users; // Use the users array from backend response
                }

                if (!quiet) {
                    dispatch('alert/setLoading', false, {root: true});
                    dispatch('alert/setLoaded', true, {root: true});
                }
                return res;
            }
            catch(err) {
                logger.error('store:microsoft-login', err);
                if (quiet) throw err;
                dispatch('alert/setErrored', true, {root: true});

                const originalResponseData = err.response && err.response.data;
                let messageForAlertModule;

                if(originalResponseData) {
                    messageForAlertModule = originalResponseData; // Default to original
                    if (originalResponseData === 'User not found in the system. Please try again or contact us at nilay@teamio.app') {
                        messageForAlertModule = `
                        <h6>There's an issue logging in, please check if any one or more of the following conditions apply:</h6>
                        <b-card class="border-0 mb-xlg background-cover">
                                <div class="d-flex">
                                <i class="glyphicon glyphicon-chevron-right mr-3"></i>
                                    <p class="fs-13 fw-semi-bold">You are logged into another Microsoft account on this browser, in this case, go to outlook.com and log out! If still having trouble, please contact Teamio support.</p>
                                </div>
                                <div class="d-flex">
                                    <i class="glyphicon glyphicon-chevron-right mr-3"></i>
                                    <p class="fs-13 fw-semi-bold">If you're a team member, make sure your Administrator / Office Manager has informed us to add you onto Teamio Account.</p>
                                </div>
                                <div class="d-flex">
                                    <i class="glyphicon glyphicon-chevron-right mr-3"></i>
                                    <p class="fs-13 fw-semi-bold">Your account may be deactivated because you may have passed the Trial Period. If this is NOT the case or you'd like to renew, please contact us right away via email at nilay@teamio.app</p>
                                </div>
                                </b-card>
                        `;
                    }
                    dispatch('alert/setMessage', messageForAlertModule, {root: true});
                } else { // Handles cases where err.response or err.response.data is null/undefined
                    dispatch('alert/setMessage', err.message || 'An unknown error occurred', {root: true});
                }
                throw err; // Throw the original err object; its err.response.data is NOT modified by this block for Login.vue's use.
            } finally {
                // return res; // <--- REMOVED THIS LINE
            }
        },

        // Sets the server-side session (Firebase claims) for the picked site. Resolves
        // { user, error } and NEVER throws (unchanged from L3, 2026-09-16: a backend hiccup must not
        // block entering a site). `user` is the caller's employee row the server returned — for a
        // Teamio operator the server creates it when the site had none (2026-09-17). Callers log in
        // with `user`, or with the picked entry only when that entry is itself a real row
        // (utils/siteSwitch.js resolveSwitchUser); a no-row stub can only be entered via `user`.
        async selectSite({commit, dispatch, rootState}, payload) {
            try {
                dispatch('alert/setLoading', true, {root: true});
                // /select-site verifies the caller's Firebase ID token and builds the session
                // claims server-side (security finding L3, 2026-09-16) — without the Bearer it
                // 401s and the chosen site's claims are never set. The helper waits out
                // Firebase's async auth init (a reload on /select-site would otherwise race it).
                const auth = rootState.getAuth();
                const idToken = await getFirebaseIdToken(rootState.getAuth());
                const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
                const body = { SiteID: payload.SiteID, EmployeeID: payload.EmployeeID, returnUser: true };
                const res = await axios.post(`/select-site`, body, { crossdomain: true, headers });
                // New claims only reach Firestore rules on the NEXT ID token. Without this refresh
                // Kanban boards and the credit pill keep checking the previous site for up to an hour.
                if (auth && auth.currentUser) {
                    await auth.currentUser.getIdToken(true);
                }
                dispatch('alert/setLoading', false, {root: true});
                dispatch('alert/setLoaded', true, {root: true});
                return { user: (res.data && res.data.user) || null, error: null };
            } catch(err) {
                console.log('Error selecting site:', err);
                dispatch('alert/setLoading', false, {root: true});
                return { user: null, error: err };
            }
        },

        // Sites the caller can switch to, fresh from the server (the login-time localStorage copy
        // goes stale). Teamio operators get every active site. Resolves null on any failure so the
        // pickers fall back to the cached list.
        async getSwitchableSites({rootState}) {
            // A demo session never switches site (the server refuses it too, decision 6a): no request.
            if (rootState.customer && rootState.customer.isDemo === true) return null;
            for (let attempt = 0; attempt < 2; attempt++) {
                try {
                    const idToken = await getFirebaseIdToken(rootState.getAuth(), attempt > 0);
                    if (!idToken) return null;
                    const res = await axios.post(`/switchable-sites`, {}, {
                        crossdomain: true,
                        headers: { Authorization: `Bearer ${idToken}` },
                    });
                    return Array.isArray(res.data) ? res.data : null;
                } catch (err) {
                    if (attempt === 0 && err.response && err.response.status === 401) continue;
                    console.log('Could not load switchable sites:', err && err.message);
                    return null;
                }
            }
            return null;
        },

        async handleSelectSite({commit, dispatch, rootState}, siteInput) {
            const site = Array.isArray(siteInput) && siteInput.length > 0 ? siteInput[0] : siteInput;
            
            try {        
        
              // Check if FirebaseUID exists before proceeding
              if (!site || !site.FirebaseUID) {
                console.error('FirebaseUID is missing from the site object');
                dispatch('alert/setErrored', true, { root: true });
                dispatch('alert/setMessage', 'Authentication error: Missing user identifier. Please try logging in again.', { root: true });
                // Optionally redirect to login or show an error message
                // router.push('/login'); 
                throw new Error('Authentication error: Missing user identifier during site switch.'); // Throw error to propagate
              }

              // Proceed with the same login flow as in Login.vue
              await dispatch('clearMetaData', site.FirebaseUID);
              await dispatch('watchMetaData', site.FirebaseUID);
        
              let s = await dispatch('getSitesAndEmployeesAndStatusesAndListsAndSettingsAndPatientsAndProviders', {
                customerID: site.CustomerID,
                siteID: site.SiteID
              });
      
      
              if(s) {
        
                let _settings = _.keyBy(s[0].data[4], 'Property');
                //console.log(`_settings: ${JSON.stringify(_settings)}`);
                let temp = _ability(site, _settings, { sites: s[0].data[0] });
      
                let host = window.location.href || '';                
      
                // Clean up localStorage but KEEP userSites for site switching functionality
                // localStorage.removeItem('userSites'); // Remove this line to preserve userSites
                localStorage.setItem('authenticated', 'true');
                localStorage.setItem('user', JSON.stringify(site));
                localStorage.setItem('teamioCustomerID', site.CustomerID);
                localStorage.setItem('teamioSiteID', site.SiteID);
                localStorage.setItem('teamioUserID', site.EmployeeID);

                // Make sure we update customer and site IDs in store
                commit('setCustomerID', site.CustomerID);
                commit('setSiteID', site.SiteID);
                commit('setCustomerID', site.CustomerID);
                commit('setUserID', site.EmployeeID);
                commit('setSiteID', site.SiteID);
                commit('setUser', site);
      
                // Demo site (plan Task 6.6, design 2d-5): EVERY demo session on prod is recorded, keyed on the prospect — never
                // the shared persona's EmployeeID — and the real-user sampling below is skipped for it. demoLogRocketPlan is null
                // for everyone else, so a real login runs the block below exactly as before (src/store/__tests__/logrocket-sampling.test.js).
                const demoRecording = demoLogRocketPlan({
                  isDemo: rootState.customer.isDemo === true,
                  host,
                  prospectID: rootState.customer.demoProspectID,
                  employeeID: site.EmployeeID,
                });
                if (demoRecording) {
                  if (demoRecording.record) {
                    if (!demoLogRocketStarted) {
                      LogRocket.init(demoRecording.appID);
                      demoLogRocketStarted = true;
                    }
                    LogRocket.identify(demoRecording.userID, demoRecording.traits);
                  }
                } else {
                  // Exclude certain sites from LogRocket
                  let siteIDsToIgnoreForLogRocket = [
                    'ad3f8022-07a1-11ed-b195-165681a6b331',
                    '231e7962-88ed-11ec-ba00-165179ac9a1d',
                    'dbee0bcb-3252-11eb-9eb6-165179ac9a1d',
                    '2dda63ae-a668-11ea-811b-16c93b6b9dbb',
                    'f4a0d034-9459-11ea-811b-16c93b6b9dbb',
                    'f6326b62-093d-11ef-bf78-165681a6b331',
                    'aa62ffca-863c-11ea-ac82-160d1264b43d'
                  ];

                  // Global default sampling rate for any site NOT listed below (0.0 to 1.0).
                  // We're on LogRocket's free plan (1,000 sessions/month). The free plan has
                  // no server-side way to cap or exclude sessions, so the only lever is to
                  // simply not call init() for the users we don't sample. Tracking 25% of
                  // employees keeps a representative sample while cutting ~75% of sessions.
                  const GLOBAL_DEFAULT_SAMPLING_RATE = 0.25;

                  // Per-site overrides (0.0 to 1.0). Lower = fewer sessions tracked.
                  // Use this for unusually high-headcount sites that would otherwise
                  // dominate the quota even at the global default rate.
                  let siteLogRocketSamplingRates = {
                    '6f946691-fb67-11ef-bf78-165681a6b331': 0.1,  // Track only 10% of users
                    // Add more site IDs as needed
                  };

                  // Internal / non-customer users we never want to record (saves quota and
                  // these sessions are rarely worth watching):
                  //  - Super Admins: our own team navigating customer sites for support
                  //  - Known test accounts
                  const logRocketTestEmails = [
                    'webprojects3064@gmail.com'
                  ];
                  function isInternalUser(s) {
                    if (s.Role === 'Super Admin') return true;
                    if (s.email && logRocketTestEmails.includes(s.email.toLowerCase())) return true;
                    return false;
                  }

                  // Function to determine if this user should be tracked. Sampling is sticky
                  // per employee (seeded by EmployeeID), so a sampled user always gets the
                  // SAME decision every login — meaning we capture their complete sessions
                  // rather than random fragments.
                  function shouldTrackUser(siteID, employeeID) {
                    const samplingRate = siteLogRocketSamplingRates[siteID] != null
                      ? siteLogRocketSamplingRates[siteID]
                      : GLOBAL_DEFAULT_SAMPLING_RATE;
                    if (samplingRate >= 1) return true;
                    if (samplingRate <= 0) return false;

                    // Use employee ID as seed for consistent sampling per user
                    // This ensures the same user always gets the same tracking decision
                    const hash = employeeID.split('').reduce((a, b) => {
                      a = ((a << 5) - a) + b.charCodeAt(0);
                      return a & a;
                    }, 0);

                    const randomValue = Math.abs(hash) % 100 / 100;
                    return randomValue < samplingRate;
                  }

                  let logRocketInitialized = false;
                  if (host && host.includes('prod.teamio.app') && site.SiteID && !siteIDsToIgnoreForLogRocket.includes(site.SiteID) && !isInternalUser(site)) {
                    // Check if this user should be tracked based on sampling rules
                    if (shouldTrackUser(site.SiteID, site.EmployeeID)) {
                      LogRocket.init('on9gpj/teamio', {
                        // Don't send LogRocket data while the user is on the internal
                        // team chat room (/chat). Those sessions are low-value, high-volume
                        // (multiple users chatting back and forth) and just eat the quota.
                        // NOTE: free plan has no URL-exclude feature, so we gate at the SDK
                        // level via shouldSendData. This stops data from being sent while on
                        // /chat; verify on a live session that chat activity doesn't appear.
                        shouldSendData: () => !window.location.pathname.includes('/chat')
                      });
                      LogRocket.log(`host: ${host} - User sampled for tracking (siteID: ${site.SiteID})`);
                      logRocketInitialized = true;
                    } else {
                      console.log(`LogRocket: User not selected for sampling (siteID: ${site.SiteID})`);
                    }
                  }

                  // Only identify user if LogRocket was initialized
                  if (logRocketInitialized) {
                    LogRocket.identify(site.EmployeeID, {
                      name: site.Name,
                      email: site.email
                    });
                  }
                }
      
                commit('setRules', temp.rules);
      
                let redirectPath = '';
      
                if (rootState.route && rootState.route.query && rootState.route.query.redirectUrl) {
                  console.log(`found query in site selection... now redirecting to ${rootState.route.query.redirectUrl}`);
                  redirectPath = rootState.route.query.redirectUrl;
                } else if (site.Role === 'Super Admin' && process.env.VUE_APP_SCHEDULE_VIEW_LANDING === 'true') {
                  // Schedule View is the Super Admin landing page while it is built (Phase 4b).
                  // Env-gated: set only in .env.development.local, because schedule-view-service has
                  // no prod stack yet — a prod landing would open on "Failed to load the schedule".
                  // Comes off at GA together with the route's meta.requiresSuperAdmin (Routes.js)
                  // and the Sidebar.vue nav gate. Guard: cypress/node-tests/post-login-redirect.test.js
                  redirectPath = '/app/schedule';
                } else if (['Admin', 'Super Admin', 'Manager'].includes(site.Role)) {
                  redirectPath = '/app/admin';
                } else {
                  // Membership in a *_RESPONSIBLE_EMPLOYEEID setting uses
                  // isInResponsibleList — the PropertyValue is pipe-delimited
                  // (e.g. "empA|empB|empC"). Prior code used strict === which
                  // only matched single-employee setups and silently failed
                  // for every multi-employee site, dropping the user on
                  // /app/todo even when they had legitimate follow-up work.
                  // See cypress/node-tests/post-login-redirect.test.js for the
                  // full Melanie/Harrisburg scenario this fix addresses.
                  const overdueList = _settings.OVERDUE_RECALL_RESPONSIBLE_EMPLOYEEID && _settings.OVERDUE_RECALL_RESPONSIBLE_EMPLOYEEID.PropertyValue;
                  const unscheduledList = _settings.UNSCHEDULED_TREATMENT_RESPONSIBLE_EMPLOYEEID && _settings.UNSCHEDULED_TREATMENT_RESPONSIBLE_EMPLOYEEID.PropertyValue;
                  const claimsList = _settings.OUTSTANDING_CLAIM_RESPONSIBLE_EMPLOYEEID && _settings.OUTSTANDING_CLAIM_RESPONSIBLE_EMPLOYEEID.PropertyValue;
                  if (isInResponsibleList(site.EmployeeID, overdueList)) {
                    redirectPath = '/app/overdue-recall';
                  } else if (isInResponsibleList(site.EmployeeID, unscheduledList)) {
                    redirectPath = '/app/unscheduled-treatment';
                  } else if (isInResponsibleList(site.EmployeeID, claimsList)) {
                    redirectPath = '/app/aging';
                  } else {
                    redirectPath = '/app/todo';
                  }
                }
      
                localStorage.setItem('redirectPath', redirectPath);

                // CLINICS FIX: Force page reload when switching sites while on the same route
                // This ensures data refreshes for the new site
                if (router.currentRoute.path === redirectPath) {
                  router.go(0);  // Force full page reload
                } else {
                  router.push({ path: redirectPath });
                }
              } else {
                console.error('getSitesAndEmployees did not return expected data');
                 dispatch('alert/setErrored', true, { root: true });
                 dispatch('alert/setMessage', 'Failed to retrieve site data. Please try again or contact support.', { root: true });
                 throw new Error('Failed to retrieve site data during site switch.');
              }
            } catch (err) {              
              console.error('Error in handleSelectSite:', err.message);
              // localStorage.removeItem('authenticated'); // Removed to prevent logout on failed switch
              // dispatch('alert/setErrored', true, { root: true }); // Already handled in component or deeper action
              // dispatch('alert/setMessage', 'An error occurred while switching sites. Please try again.', { root: true }); // Component will show more specific message
              throw err; // Re-throw the error to be caught by the component
            }
          },


        async login({commit, dispatch, rootState}, user) {
            try {
                /*
                let resp = await axios.post(`/login`, user, { crossdomain: true });
                const token = resp.data.token;
                const user = resp.data.user;
                */

                // Simulating async call to external service
                /*
                await new Promise(resolve => setTimeout(resolve, 2000));
                const token = 'this is a test';
                const user = {
                    'name'  : 'Saumil Shah',
                    'email' : 'shahsaumil@gmail.com',
                };
                */

                let loggedinUser = await rootState.auth.signInWithEmailAndPassword(user.email, user.password);
                //let employee = await dispatch('customer/getEmployee', { AuthUID: loggedinUser.user.uid}, {root: true});
                //await dispatch('customer/getSites', { customerID: employee.CustomerID}, {root: true});

                const newUser = {
                    authUID: loggedinUser.user.uid,
                    userID: employee.id,
                    customerID: employee.CustomerID,
                    siteID: rootState.customer.siteID,
                    name: loggedinUser.user.displayName,
                    email: loggedinUser.user.email,
                    photoUrl: loggedinUser.user.photoURL
                }
                commit('setUser', newUser);
                
                //localStorage.setItem('token', token);
                localStorage.setItem('authenticated', 'true');
                localStorage.setItem('teamioCustomerID', newUser.customerID);
                localStorage.setItem('teamioSiteID', newUser.siteID);
                localStorage.setItem('teamioUserID', newUser.userID);
                //axios.defaults.headers.common['Authorization'] = token;
                
                router.push({name: 'YourTasks'});
            }
            catch(err) {
                console.log('Error during Login:', err);
                //localStorage.removeItem('token');
                router.push('/login');
            }
        },

        // Demo site (Try Teamio, plan Task 6.2): the prospect opens the operator's one-time link (/demo#t=<code>,
        // demo-service scripts/make-demo-link.js). POST /demo/redeem → a demo custom token + the owner persona row (the
        // microsoftLogin shape) → sign in → the flag FROM THE NEW TOKEN → the post-login path every login uses.
        // Throws { code: 'existing_session' } while another practice is signed in: the page asks first and logs out.
        async demoLogin({commit, dispatch, state}, { code }) {
            if (localStorage.getItem('authenticated') === 'true' && !state.isDemo) {
                const err = new Error('Signed in to another practice');
                err.code = 'existing_session';
                throw err;
            }
            dispatch('alert/setLoading', true, {root: true});
            try {
                const res = await axios.post('/demo/redeem', { code }, { crossdomain: true });
                const { getAuth, signInWithCustomToken, signOut } = await import('firebase/auth');
                const auth = getAuth();
                const cred = await signInWithCustomToken(auth, res.data.firebaseToken);
                const { claims } = await cred.user.getIdTokenResult();
                if (!isDemoClaims(claims)) {
                    await signOut(auth);
                    throw new Error('The demo sign-in did not return a demo session');
                }
                commit('setIsDemo', true);
                commit('setDemoProspectID', claims.prospectID);
                localStorage.removeItem('userSites');
                dispatch('alert/setLoading', false, {root: true});
                await dispatch('handleSelectSite', res.data.users);
                return res.data.users;
            } catch (err) {
                dispatch('alert/setLoading', false, {root: true});
                throw err;
            }
        },

        // Demo site (plan Task 6.4): the banner's "View as". POST /demo/switch-role (the interceptor sends the CURRENT
        // demo token; the server owns the role) → the same tail as demoLogin. The old persona's Firestore listeners are
        // cleared only once the new sign-in has succeeded, so a failed switch leaves the session as it was.
        async demoSwitchRole({commit, dispatch, state}, { persona }) {
            if (!state.isDemo) throw new Error('Not a demo session');
            dispatch('alert/setLoading', true, {root: true});
            try {
                const res = await axios.post('/demo/switch-role', { persona }, { crossdomain: true });
                const { getAuth, signInWithCustomToken, signOut } = await import('firebase/auth');
                const auth = getAuth();
                const cred = await signInWithCustomToken(auth, res.data.firebaseToken);
                const { claims } = await cred.user.getIdTokenResult();
                if (!isDemoClaims(claims)) {
                    await signOut(auth);
                    throw new Error('The role switch did not return a demo session');
                }
                commit('clearFirestoreListeners');
                commit('setIsDemo', true);
                commit('setDemoProspectID', claims.prospectID);
                localStorage.removeItem('userSites');
                dispatch('alert/setLoading', false, {root: true});
                await dispatch('handleSelectSite', res.data.users);
                return res.data.users;
            } catch (err) {
                dispatch('alert/setLoading', false, {root: true});
                throw err;
            }
        },

        // Demo site (plan Task 6.4): "Simulate a patient reply" in a sample patient's thread. The reply arrives through
        // the thread's normal live listener; the server picks the site and refuses a thread that is not a sample patient's.
        async demoSimulateReply({state}, { conversationId }) {
            if (!state.isDemo) throw new Error('Not a demo session');
            const res = await axios.post('/demo/simulate-inbound', { conversationId }, { crossdomain: true });
            return res.data;
        },

        async logout( {commit, dispatch, rootState}, payload=null){

            /*
                This below check was added because even for a public page, it was redirecting to login because in firebase.config.js file, firebase.auth().onIdTokenChanged gets called which ends up redirecting to logout
                but we don't want to log out if this came from view-form
            */
            let startLocation = router.history['_startLocation'];
            //console.log(`startLocation: ${startLocation}`);

            if(startLocation.indexOf('/view-form/') === -1 && startLocation.indexOf('/l/') === -1 
                && startLocation.indexOf('/view-submission-for-pdf/') === -1                
                ) { 
                try {
                    // CRITICAL: Clean up all Firestore listeners BEFORE signing out
                    // to prevent permission-denied errors from orphaned listeners
                    commit('clearFirestoreListeners');

                    try {
                        await dispatch('conversationsV2/detachAll', null, { root: true });
                    } catch (error) {
                        // May not be initialized, ignore
                    }

                    try {
                        await dispatch('wallet/teardownBalance', null, { root: true });
                    } catch (error) {
                        // May not be initialized, ignore
                    }

                    // Demo site (Try Teamio): read BEFORE signOut, which clears the flag (firebase.config.js).
                    const demoSession = !!(rootState.customer && rootState.customer.isDemo === true);
                    const auth = rootState.getAuth();
                    await rootState.signOut(auth);
                    commit('setIsDemo', false);
                    commit('setDemoProspectID', null);
                    localStorage.removeItem('authenticated');
                    localStorage.removeItem('userSites');
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('formverified');
                    localStorage.removeItem('user');
                    localStorage.removeItem('teamioCustomerID');
                    localStorage.removeItem('teamioSiteID');
                    localStorage.removeItem('teamioUserID');
                    localStorage.removeItem('statuses');
                    localStorage.removeItem('lists');
                    localStorage.removeItem('sites');
                    localStorage.removeItem('settings');
                    localStorage.removeItem('employees');
                    localStorage.removeItem('patients');
                    // Header search "Recently viewed" (utils/recentPatients.js RECENT_PATIENTS_KEY):
                    // the next person on a shared tablet starts with an empty list.
                    localStorage.removeItem('teamio_recent_patients');
                    localStorage.removeItem('providers');
                    localStorage.removeItem('rules');
                    localStorage.removeItem('redirectPath');
                    localStorage.removeItem('brokenApptAdjustments');
                    localStorage.removeItem('brokenApptCodes');
                    delete axios.defaults.headers.common['Authorization'];

                    // SHARED-COMPUTER FULL LOGOUT (Microsoft only).
                    // The Firebase signOut above ends the *Teamio* session, but not the
                    // identity provider's own browser session. For Microsoft we can end
                    // it via MSAL (microsoftAuth.logout() no-ops when there are no MSAL
                    // accounts, i.e. Google users — Google has no supported third-party
                    // logout). Only do this on a USER-INITIATED Log Out: the automatic
                    // reload paths (token-revoked / version-update) have no user gesture,
                    // so a logout popup would be blocked and is disruptive for a
                    // background logout. Best-effort — a blocked/closed popup must never
                    // block the Teamio logout + redirect below. A demo session has no Microsoft account.
                    const userInitiated = !(payload && payload.reload === true);
                    if (userInitiated && !demoSession) {
                        try {
                            await microsoftAuth.logout();
                        } catch (msalErr) {
                            console.warn(`Microsoft IdP logout skipped (best-effort): ${msalErr && msalErr.message ? msalErr.message : msalErr}`);
                        }
                    }

                    let loc = window.location.href;
                    //console.log(`loc: ${loc}`);

                    if(payload && payload.reload === true) {
                        // Token-revoked / nightly / version-update logout — we
                        // need the user to land on the LATEST index.html and
                        // JS bundles. Hard-navigate (not SPA), plus append a
                        // ?v= cache-buster so CloudFront can't serve a stale
                        // index.html during the brief window after a deploy
                        // invalidation is still propagating. index.html is
                        // already no-cache, but defense-in-depth here — a
                        // unique query string guarantees a fresh origin fetch
                        // regardless of edge cache state.
                        const msg = encodeURIComponent(payload.msg || '');
                        const v = Date.now();
                        let qs = msg ? `?v=${v}&msg=${msg}` : `?v=${v}`;
                        // Session-health "sign in again" (src/plugins/sessionHealthGuard.js): land
                        // back on the page they were on. handleSelectSite already honours
                        // ?redirectUrl= (the same param the route guard sets). Same-origin paths only.
                        if (typeof payload.redirectUrl === 'string' && /^\/(?!\/)/.test(payload.redirectUrl)) {
                            qs += `&redirectUrl=${encodeURIComponent(payload.redirectUrl)}`;
                        }
                        window.location.href = logoutPath(demoSession, payload.next) + qs;
                    } else if (demoSession || loc.indexOf('login') === -1) {
                        // Normal logout - also hard navigate to ensure fresh assets (a demo session → /try)
                        window.location.href = logoutPath(demoSession);
                    }
                } catch(err) {
                    console.error(`Error while logging out: ${err}`);
                }
            }
        }
    }
}